import { EditorView } from "@codemirror/view";
import type { Extension } from "@uiw/react-codemirror";
import { useMemo } from "react";

import { CodeMirror } from "metabase/common/components/CodeMirror";

import type { CodeLanguage } from "./types";
import { useExtensions } from "./utils";

type Props = {
  className?: string;
  highlightRanges?: { start: number; end: number }[];
  id?: string;
  "aria-labelledby"?: string;
  language?: CodeLanguage | Extension;
  lineNumbers?: boolean;
  readOnly?: boolean;
  value: string;
  proposedValue?: string;
  onChange?: (value: string) => void;
  extensions?: Extension[];
  "data-testid"?: string;
};

export function CodeEditor({
  className,
  highlightRanges,
  id,
  "aria-labelledby": ariaLabelledBy,
  language,
  lineNumbers = true,
  readOnly,
  value,
  proposedValue,
  onChange,
  extensions: externalExtensions,
  ...rest
}: Props) {
  const languageExtensions = useExtensions({
    language,
    extensions: externalExtensions,
    originalValue: proposedValue ? value : undefined,
    proposedValue,
  });

  const extensions = useMemo(() => {
    const contentAttrs: Record<string, string> = {};
    if (id) {
      contentAttrs.id = id;
    }
    if (ariaLabelledBy) {
      contentAttrs["aria-labelledby"] = ariaLabelledBy;
    }
    if (Object.keys(contentAttrs).length === 0) {
      return languageExtensions;
    }
    // Apply name/id on the contenteditable textbox, not the wrapper div.
    return [
      ...languageExtensions,
      EditorView.contentAttributes.of(contentAttrs),
    ];
  }, [languageExtensions, id, ariaLabelledBy]);

  const basicSetup = useMemo(
    () => ({
      lineNumbers,
      foldGutter: false,
      highlightActiveLine: false,
      highlightActiveLineGutter: false,
    }),
    [lineNumbers],
  );

  return (
    <CodeMirror
      basicSetup={basicSetup}
      className={className}
      // Jest mock maps this onto the textarea; real DOM uses contentAttributes only.
      data-editor-labelledby={ariaLabelledBy}
      extensions={extensions}
      editable={!readOnly}
      readOnly={readOnly}
      value={proposedValue ?? value}
      onChange={onChange}
      highlightRanges={highlightRanges}
      {...rest}
    />
  );
}
